/**
 * What Rowted sets for itself — `docs/rowted-admin-v2.md` §5.4, board 7.
 *
 * Two columns of cards, and every row on them is the same shape: a name, a
 * clause saying what it decides, the value as it stands, and **Change**. That
 * repetition is the point — this is the screen where somebody re-prices the
 * product and turns off a client's AI, and a page where each setting had its
 * own form would be a page where each setting had its own surprises.
 *
 * **Nothing here reaches a client who already signed up.** The price list, the
 * trial length and the feature set are copied onto the tenant at signup
 * (`services/signup.ts`), so a change decides what the *next* one gets. The
 * page says so in as many words, because the fear when editing a price list is
 * that you have just re-priced everybody.
 *
 * **Every save is a step-up.** `useStepUp` holds the action, asks for the
 * code, then runs it — the same guard the Integrations flyout uses, so the two
 * screens ask the same way.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  ApiError, CORE_FEATURES, SELLABLE_FEATURES, featureWords, featuresOf,
} from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import {
  Button, Checkbox, SectionLabel, Switch, Tag, type TagTone,
} from '../../components/primitives.js'
import { Field, SelectField } from '../../components/fields.js'
import { Modal } from '../../components/overlays.js'
import { PageBar } from '../../frame/page-bar.js'
import { cn } from '../../lib/cn.js'
import { IconCheck, IconSpinner, IconWarn } from '../../frame/icons.js'
import { AdminPanel, AdminRow, AdminRows } from './panel.js'
import { AdminPage, Loading, can, useAdminUser } from './shell.js'
import { useStepUp } from './step-up.js'

// ---------------------------------------------------------------------------
// What the server answers
// ---------------------------------------------------------------------------

interface Tier {
  key: string
  name: string
  baseCents: number
  seatCents: number
  includedSeats: number
  stripePriceId?: string
  features?: string[]
}

interface Settings {
  tiers: { version: string; currency: string; tiers: Tier[] }
  trials: { days: number; cardRequired: boolean; nudgeDays: number[] }
  demo: { datasetVersion: string; idleMinutes: number; wipeOnSignOut: boolean }
  identity: {
    productName: string; supportAddress: string; appUrl: string; apiUrl: string
    logoKey: string | null; accentHex: string
  }
  system: {
    maintenanceBanner: { on: boolean; text: string; readOnly: boolean }
    aiEnabled: boolean
    clientSignIn: { officeMfa: 'required' | 'optional'; crewPin: boolean }
    retention: { auditYears: number; filesAfterCloseYears: number; demoDays: number }
    sessionIdleHours: number
    inviteDomains: string[]
  }
}

interface IdentityCheck {
  name: string; lookedUp: string; status: 'ok' | 'warn' | 'bad' | 'unknown'
  detail: string; record?: string
}
interface EmailIdentity { domain: string | null; checkedAt: string; checks: IdentityCheck[] }

/** `GET /platform/backups` — the last drill, and whether one can be run here. */
interface BackupStatus {
  configured: boolean
  missing: string[]
  tone: 'ok' | 'warn' | 'bad' | 'off'
  note: string
  lastRun: {
    at: string; ok: boolean; seconds: number; dumpBytes: number
    tenants: number; tablesCompared: number; failures: string[]; byName: string | null
  } | null
}

type SettingKey = keyof Settings

/** Which dialog is open. Null is none; the page never opens two. */
type Editing =
  | null
  | { kind: 'tiers' }
  | { kind: 'features' }
  | { kind: 'trials' }
  | { kind: 'demo' }
  | { kind: 'identity' }
  | { kind: 'banner' }
  | { kind: 'ai' }
  | { kind: 'signIn' }
  | { kind: 'retention' }
  /* Admin access — Rowted's own staff, not a client's (v3 §3.1). */
  | { kind: 'invites' }
  | { kind: 'idle' }

/** The settings the one small dialog edits, keyed by which panel opened it. */
type SystemEditor = 'banner' | 'ai' | 'signIn' | 'retention' | 'invites' | 'idle'

const money = (cents: number) => `$${(cents / 100).toLocaleString(undefined, {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`

export function AdminSettings() {
  const { me, ready } = useAdminUser()
  const stepUp = useStepUp()
  const [settings, setSettings] = useState<Settings | null>(null)
  const [identity, setIdentity] = useState<EmailIdentity | null>(null)
  const [checking, setChecking] = useState(false)
  const [backups, setBackups] = useState<BackupStatus | null>(null)
  const [drilling, setDrilling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Editing>(null)

  const load = useCallback(async () => {
    try {
      const got = await adminRequest<{ settings: Settings }>('/settings')
      setSettings(got.settings)
      /*
       * Loaded with the page, unlike the DNS checks: this is one row out of
       * `platform_settings`, not three lookups on somebody else's
       * nameservers.
       */
      setBackups(await adminRequest<BackupStatus>('/backups').catch(() => null))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not load.')
    }
  }, [])

  /*
   * The DNS lookups, on their own and not on load: three of them take a
   * second or two, and a settings page that waits on somebody else's
   * nameservers to open is a settings page that feels broken.
   */
  const recheck = useCallback(async () => {
    setChecking(true)
    try {
      setIdentity(await adminRequest<EmailIdentity>('/settings/email-identity'))
    } catch { setIdentity(null) } finally { setChecking(false) }
  }, [])

  /**
   * Queue a drill, and say so.
   *
   * Queued rather than awaited: it dumps, restores and walks every table of
   * every tenant, and a request that waited on that would time out long
   * before it finished. The row updates when the job records its result,
   * which is the next time this page is opened.
   */
  const drill = useCallback(async () => {
    setDrilling(true)
    setError(null)
    try {
      await adminRequest('/backups/drill', { method: 'POST', body: {} })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That could not be started.')
    } finally { setDrilling(false) }
  }, [])

  useEffect(() => { if (ready && me) void load() }, [ready, me, load])

  /**
   * One save for every row on the page.
   *
   * `PUT /settings/:key` takes the whole key, so each dialog hands back a
   * complete value rather than a patch — which is also what makes a
   * half-saved setting impossible.
   */
  const save = useCallback((key: SettingKey, value: unknown) => {
    setError(null)
    void stepUp.guard(async () => {
      await adminRequest(`/settings/${key}`, { method: 'PUT', body: { value } })
    })
      .then((ran) => { if (ran) { setEditing(null); void load() } })
      .catch((caught: unknown) => setError(
        caught instanceof ApiError ? caught.message : 'That did not save.'))
  }, [load, stepUp])

  if (!ready || (!settings && !error)) {
    return <AdminPage me={me} active="settings"><Loading /></AdminPage>
  }
  if (!settings) {
    return (
      <AdminPage me={me} active="settings">
        <p role="alert" className="m-0 p-4 text-body text-danger">{error}</p>
      </AdminPage>
    )
  }

  const mayEdit = can(me, 'settings')
  const mayPrice = can(me, 'pricing')
  const open = (next: Editing) => () => setEditing(next)

  return (
    <AdminPage me={me} active="settings">
      <PageBar
        title="Settings"
        sub="what the next client gets"
        variant="page"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-6 pb-6 pt-4">
        {error && (
          <p role="alert" className="m-0 rounded-card bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}

        <div className="grid items-start gap-3.5 xl:grid-cols-2">
          <div className="flex flex-col gap-3.5">
            <Plans
              tiers={settings.tiers}
              mayEdit={mayPrice}
              onEdit={open({ kind: 'tiers' })}
              onFeatures={open({ kind: 'features' })}
            />

            <AdminPanel title="Trials and demo">
              <AdminRows>
                <ChangeRow
                  name="Trial length"
                  sub="applies to the next sign-up"
                  value={`${settings.trials.days} days`}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'trials' })}
                />
                <ChangeRow
                  name="Card up front"
                  value={settings.trials.cardRequired ? 'On' : 'Off'}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'trials' })}
                />
                <ChangeRow
                  name="Nudge emails"
                  sub="days after sign-up"
                  value={settings.trials.nudgeDays.join(' · ') || 'none'}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'trials' })}
                />
                <ChangeRow
                  name="Demo dataset"
                  sub="rebuilt nightly · wiped after idle"
                  value={`${settings.demo.datasetVersion} · ${settings.demo.idleMinutes} min`}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'demo' })}
                />
              </AdminRows>
            </AdminPanel>
          </div>

          <div className="flex flex-col gap-3.5">
            <AdminPanel title="Product identity">
              <AdminRows>
                <ChangeRow
                  name="Product name"
                  sub="shown in emails, the portal and sign-up"
                  value={settings.identity.productName}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'identity' })}
                />
                <ChangeRow
                  name="Support address"
                  sub="reply-to on every email · shown in the app"
                  value={settings.identity.supportAddress || 'not set'}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'identity' })}
                />
                <ChangeRow
                  name="App and API domains"
                  value={[settings.identity.appUrl, settings.identity.apiUrl]
                    .filter(Boolean).map(host).join(' · ') || 'not set'}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'identity' })}
                />
                <ChangeRow
                  name="Logo and colours"
                  sub="the platform brand, not a client’s"
                  value={(
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="inline-block h-3 w-3 rounded-[3px] border border-line"
                        style={{ background: settings.identity.accentHex }}
                      />
                      {settings.identity.logoKey ?? 'no logo'} · {settings.identity.accentHex}
                    </span>
                  )}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'identity' })}
                />
              </AdminRows>
            </AdminPanel>

            {/*
              * **Admin access** — v3 §3.1 moved these here from the Admin
              * users page, where they were read-only beside the list.
              *
              * A switch that is read on one screen and saved on another is a
              * switch in the wrong place: these now sit with the settings
              * they describe, and Admin users is the people.
              *
              * The first two are drawn and cannot be moved. §6.12: an account
              * that can read every customer does not get to decide one factor
              * is enough. Drawn rather than left out, because a card silent
              * about the most important rule on it leaves somebody wondering
              * whether it is set — and disabled rather than a sentence, so the
              * row is the same shape as the ones beside it.
              */}
            <AdminPanel title="Admin access" note="Rowted's own staff">
              <AdminRows>
                <FixedRow
                  label="Two-step sign-in required"
                  sub="an authenticator app · no SMS fallback for platform accounts"
                />
                <FixedRow
                  label="Sign-in code again for sensitive actions"
                  sub="keys, signing in as a client, admin changes — after 10 minutes"
                />
                <ChangeRow
                  name="Who can be invited"
                  sub={settings.system.inviteDomains.length
                    ? 'only these domains'
                    : 'no domain is set, so an invite can go anywhere'}
                  value={settings.system.inviteDomains.length
                    ? settings.system.inviteDomains.join(', ')
                    : 'Any address'}
                  tone={settings.system.inviteDomains.length ? undefined : 'warn'}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'invites' })}
                />
                <ChangeRow
                  name="Sign an admin out when idle"
                  sub="every Rowted Admin session, on every device"
                  value={`${settings.system.sessionIdleHours} hours`}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'idle' })}
                />
              </AdminRows>
            </AdminPanel>

            <AdminPanel title="System">
              <AdminRows>
                <ChangeRow
                  name="Maintenance banner"
                  sub="shown to every client · optional read-only mode"
                  value={settings.system.maintenanceBanner.on
                    ? (settings.system.maintenanceBanner.readOnly ? 'On · read-only' : 'On')
                    : 'Off'}
                  tone={settings.system.maintenanceBanner.on ? 'warn' : undefined}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'banner' })}
                />
                <ChangeRow
                  name="Rowted AI"
                  sub="hidden everywhere until Azure OpenAI is connected"
                  value={settings.system.aiEnabled ? 'On' : 'Off'}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'ai' })}
                />
                <ChangeRow
                  name="Client sign-in policy"
                  sub="what every client’s users must do"
                  value={[
                    settings.system.clientSignIn.officeMfa === 'required'
                      ? 'MFA for office roles' : 'MFA optional',
                    settings.system.clientSignIn.crewPin ? 'PIN for crew' : 'no crew PIN',
                  ].join(' · ')}
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'signIn' })}
                />
                <ChangeRow
                  name="Data retention"
                  sub={`audit ${settings.system.retention.auditYears} y · files `
                    + `${settings.system.retention.filesAfterCloseYears} y after close · demo `
                    + `${settings.system.retention.demoDays} d`}
                  value="Defaults"
                  mayEdit={mayEdit}
                  onEdit={open({ kind: 'retention' })}
                />
                <Backups
                  status={backups}
                  mayRun={mayEdit}
                  running={drilling}
                  onRun={() => void drill()}
                />
                <MailIdentity
                  identity={identity}
                  checking={checking}
                  onCheck={() => void recheck()}
                />
              </AdminRows>
            </AdminPanel>
          </div>
        </div>
      </div>

      {editing?.kind === 'tiers' && (
        <PlansDialog
          tiers={settings.tiers}
          onClose={() => setEditing(null)}
          onSave={(value) => save('tiers', value)}
        />
      )}
      {editing?.kind === 'features' && (
        <FeaturesDialog
          tiers={settings.tiers}
          onClose={() => setEditing(null)}
          onSave={(value) => save('tiers', value)}
        />
      )}
      {editing?.kind === 'trials' && (
        <TrialsDialog
          trials={settings.trials}
          onClose={() => setEditing(null)}
          onSave={(value) => save('trials', value)}
        />
      )}
      {editing?.kind === 'demo' && (
        <DemoDialog
          demo={settings.demo}
          onClose={() => setEditing(null)}
          onSave={(value) => save('demo', value)}
        />
      )}
      {editing?.kind === 'identity' && (
        <IdentityDialog
          identity={settings.identity}
          onClose={() => setEditing(null)}
          onSave={(value) => save('identity', value)}
        />
      )}
      {editing && ['banner', 'ai', 'signIn', 'retention', 'invites', 'idle']
        .includes(editing.kind) && (
        <SystemDialog
          which={editing.kind as SystemEditor}
          system={settings.system}
          onClose={() => setEditing(null)}
          onSave={(value) => save('system', value)}
        />
      )}
      {stepUp.dialog}
    </AdminPage>
  )
}

/** `https://app.rowted.com` → `app.rowted.com`, which is what a person reads. */
function host(url: string): string {
  try { return new URL(url).host } catch { return url }
}

// ---------------------------------------------------------------------------
// The repeating shapes
// ---------------------------------------------------------------------------

/**
 * A rule nobody can turn off — the two at the top of Admin access.
 *
 * Shaped like `ChangeRow` on purpose, minus the button: the eye reads down a
 * column of settings, and a rule drawn as a sentence instead of a row is a
 * rule that looks like commentary.
 */
function FixedRow({ label, sub }: { label: string; sub: string }) {
  return (
    <AdminRow>
      <span className="min-w-0 flex-1">
        <span className="block text-cell font-medium text-text">{label}</span>
        <span className="block text-[11.5px] leading-[1.45] text-text-faint">{sub}</span>
      </span>
      <span className="shrink-0 text-cell text-text-muted">Always</span>
      <Switch checked disabled onChange={() => {}} label={label} />
    </AdminRow>
  )
}

/**
 * One setting: what it is, what it decides, where it stands, and Change.
 *
 * **Change is absent, not disabled, for a role that may not.** §2.3 decides
 * what a role can do and `shared/src/platform-roles.ts` is the same table the
 * server checks; a greyed button on every row would be a screen inviting
 * somebody to keep trying.
 */
function ChangeRow({ name, sub, value, tone, mayEdit, onEdit }: {
  name: string
  sub?: string
  value: ReactNode
  tone?: TagTone
  mayEdit: boolean
  onEdit: () => void
}) {
  return (
    <AdminRow>
      <span className="min-w-0 flex-1">
        <span className="block text-cell font-medium text-text">{name}</span>
        {sub && <span className="block text-[11.5px] leading-[1.45] text-text-faint">{sub}</span>}
      </span>
      <span className="shrink-0 text-cell text-text-body">
        {tone ? <Tag tone={tone}>{value}</Tag> : value}
      </span>
      {mayEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="shrink-0 text-cell text-accent hover:text-accent-dark hover:underline"
        >
          Change
        </button>
      )}
    </AdminRow>
  )
}

// ---------------------------------------------------------------------------
// Plans and pricing
// ---------------------------------------------------------------------------

/**
 * Board 7's price table.
 *
 * Rows rather than a `<table>`: the columns are fixed, nobody sorts or filters
 * them, and §7 asks that nothing under `pages/admin/` hand-roll a table.
 */
function Plans({ tiers, mayEdit, onEdit, onFeatures }: {
  tiers: Settings['tiers']
  mayEdit: boolean
  onEdit: () => void
  onFeatures: () => void
}) {
  return (
    <AdminPanel
      title="Plans and pricing"
      note={`${tiers.version} · ${tiers.currency}`}
      action={mayEdit ? <Button onClick={onEdit}>New price list</Button> : undefined}
    >
      <div className="grid grid-cols-[1.2fr_repeat(3,auto)_1.4fr] gap-x-3 gap-y-0 px-4 py-2">
        <SectionLabel>Tier</SectionLabel>
        <SectionLabel className="text-right">Base / mo</SectionLabel>
        <SectionLabel className="text-right">Per seat</SectionLabel>
        <SectionLabel className="text-right">Included</SectionLabel>
        <SectionLabel>Features</SectionLabel>
      </div>

      <ul className="m-0 list-none p-0">
        {tiers.tiers.map((tier) => (
          <li
            key={tier.key}
            className="grid grid-cols-[1.2fr_repeat(3,auto)_1.4fr] items-baseline gap-x-3
                       border-t border-line-soft px-4 py-2.5 text-cell"
          >
            <span className="min-w-0">
              <span className="block truncate font-semibold text-text">{tier.name}</span>
              <span className="block truncate text-[11px] text-text-faint">
                {tier.stripePriceId ?? 'no Stripe price'}
              </span>
            </span>
            <span className="tnum text-right text-text">{money(tier.baseCents)}</span>
            <span className="tnum text-right text-text-body">{money(tier.seatCents)}</span>
            <span className="tnum text-right text-text-body">{tier.includedSeats}</span>
            <span className="min-w-0 truncate text-[11.5px] text-text-muted">
              {featureWords(tier.features)}
            </span>
          </li>
        ))}
      </ul>

      <footer className="flex items-center gap-2 border-t border-line-soft bg-surface-alt
                         px-4 py-2.5">
        <span className="min-w-0 flex-1 text-[11.5px] leading-[1.45] text-text-muted">
          Features per tier decide what a client’s Settings › Features may turn on. A new
          price list applies to the next sign-up; existing clients keep theirs.
        </span>
        {mayEdit && <Button onClick={onFeatures}>Edit features by tier</Button>}
      </footer>
    </AdminPanel>
  )
}

function PlansDialog({ tiers, onClose, onSave }: {
  tiers: Settings['tiers']
  onClose: () => void
  onSave: (value: unknown) => void
}) {
  const [draft, setDraft] = useState(tiers)

  const set = (index: number, patch: Partial<Tier>) => setDraft((was) => {
    const next = [...was.tiers]
    const tier = next[index]
    if (!tier) return was
    next[index] = { ...tier, ...patch }
    return { ...was, tiers: next }
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="New price list"
      subtitle="applies to the next sign-up"
      width={700}
      footerNote="Existing clients keep the price they signed up on. Saving asks for your code."
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onSave(draft)}>Save price list</Button>
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Version"
          value={draft.version}
          onChange={(version) => setDraft({ ...draft, version })}
          hint="Stamped on every subscription priced from it, so two customers' invoices
                can be told apart a year from now."
        />

        {draft.tiers.map((tier, index) => (
          <div key={tier.key} className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
            <SectionLabel>{tier.name}</SectionLabel>
            <div className="grid gap-2 sm:grid-cols-2">
              <Money
                label="Base per month"
                cents={tier.baseCents}
                onChange={(baseCents) => set(index, { baseCents })}
              />
              <Money
                label="Per extra seat"
                cents={tier.seatCents}
                onChange={(seatCents) => set(index, { seatCents })}
              />
              <Field
                label="Seats included"
                value={String(tier.includedSeats)}
                onChange={(raw) => set(index, {
                  includedSeats: Math.max(0, Math.round(Number(raw.replace(/[^0-9]/g, '')) || 0)),
                })}
              />
              <Field
                label="Stripe price"
                value={tier.stripePriceId ?? ''}
                onChange={(stripePriceId) => set(index, { stripePriceId })}
                placeholder="price_1P…"
              />
            </div>
          </div>
        ))}
      </div>
    </Modal>
  )
}

/** Dollars in the box, cents in the value — the only place they are converted. */
function Money({ label, cents, onChange }: {
  label: string; cents: number; onChange: (cents: number) => void
}) {
  return (
    <Field
      label={label}
      value={String(cents / 100)}
      onChange={(raw) => onChange(Math.round((Number(raw.replace(/[^0-9.]/g, '')) || 0) * 100))}
      hint={money(cents)}
    />
  )
}

/**
 * Which features each tier sells — §5.4.
 *
 * A grid rather than one list per tier, because the question somebody has
 * while editing this is always comparative: *does Growth get rentals when
 * Starter does not*. Core features are not offered, because they are true for
 * everybody and a tickbox that cannot be unticked is furniture.
 */
function FeaturesDialog({ tiers, onClose, onSave }: {
  tiers: Settings['tiers']
  onClose: () => void
  onSave: (value: unknown) => void
}) {
  const [draft, setDraft] = useState(tiers)

  const toggle = (index: number, key: string) => setDraft((was) => {
    const next = [...was.tiers]
    const tier = next[index]
    if (!tier) return was
    const have = new Set(featuresOf(tier.features))
    if (have.has(key)) have.delete(key)
    else have.add(key)
    next[index] = {
      ...tier,
      features: [...have].filter((one) => !CORE_FEATURES.includes(one)),
    }
    return { ...was, tiers: next }
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="Features by tier"
      subtitle="what a client’s Settings › Features may turn on"
      width={700}
      footerNote="Applies to the next sign-up. A client already on a tier keeps what they have."
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onSave(draft)}>Save features</Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-cell leading-[1.45] text-text-muted">
          Quoting, scheduling and invoicing are in every tier — a field-service company
          cannot run without them, so they are not offered here.
        </p>

        {/*
          * No column gap: each cell draws its own top border, and a gap between
          * the columns broke every rule into pieces with a notch where the
          * names end. The spacing is padding on the cells instead.
          */}
        <div
          className="grid items-baseline"
          style={{ gridTemplateColumns: `1.6fr repeat(${draft.tiers.length}, 84px)` }}
        >
          <span />
          {draft.tiers.map((tier) => (
            <span key={tier.key}
              className="px-1 text-center text-[10.5px] font-semibold uppercase
                         tracking-[0.05em] text-text-faint">
              {tier.name}
            </span>
          ))}

          {SELLABLE_FEATURES.map((feature) => (
            <Fragmentish key={feature.key}>
              <span className="border-t border-line-soft py-2 pr-3">
                <span className="block text-cell font-medium text-text">{feature.label}</span>
                <span className="block text-[11px] leading-[1.4] text-text-faint">
                  {feature.sub}
                  {feature.needs && ' · needs the integration connected'}
                </span>
              </span>
              {draft.tiers.map((tier, index) => (
                <span key={tier.key}
                  className="flex justify-center border-t border-line-soft py-2">
                  <Checkbox
                    hideLabel
                    label={`${tier.name}: ${feature.label}`}
                    checked={featuresOf(tier.features).includes(feature.key)}
                    onChange={() => toggle(index, feature.key)}
                  />
                </span>
              ))}
            </Fragmentish>
          ))}
        </div>
      </div>
    </Modal>
  )
}

/**
 * A grid row that is several cells.
 *
 * `<>…</>` would do, but a keyed fragment cannot carry the key *and* read
 * clearly at the call site; this names what it is instead.
 */
const Fragmentish = ({ children }: { children: ReactNode }) => <>{children}</>

// ---------------------------------------------------------------------------
// The small dialogs
// ---------------------------------------------------------------------------

function TrialsDialog({ trials, onClose, onSave }: {
  trials: Settings['trials']; onClose: () => void; onSave: (value: unknown) => void
}) {
  const [draft, setDraft] = useState(trials)
  const nudges = useMemo(() => draft.nudgeDays.join(', '), [draft.nudgeDays])
  const [typed, setTyped] = useState(nudges)

  return (
    <SmallDialog
      title="Trials"
      note="Applies to the next sign-up. A trial already running keeps its dates."
      onClose={onClose}
      onSave={() => onSave({
        ...draft,
        /* Sorted and de-duplicated here as well as on the server, so the box
           somebody typed into reads back the way it will be stored. */
        nudgeDays: [...new Set(typed.split(/[^0-9]+/).map(Number)
          .filter((day) => day >= 1 && day <= 365))].sort((a, b) => a - b),
      })}
    >
      <Field
        label="Trial length"
        value={String(draft.days)}
        onChange={(raw) => setDraft({
          ...draft, days: Math.min(365, Math.round(Number(raw.replace(/[^0-9]/g, '')) || 0)),
        })}
        hint="Days from sign-up."
      />
      <SwitchRow
        label="Card up front"
        checked={draft.cardRequired}
        onChange={(cardRequired) => setDraft({ ...draft, cardRequired })}
        hint="On means a trial cannot start without a card on file."
      />
      <Field
        label="Nudge emails"
        value={typed}
        onChange={setTyped}
        hint="Days after sign-up, comma separated — 3, 7, 12, 14, 20."
      />
    </SmallDialog>
  )
}

function DemoDialog({ demo, onClose, onSave }: {
  demo: Settings['demo']; onClose: () => void; onSave: (value: unknown) => void
}) {
  const [draft, setDraft] = useState(demo)
  return (
    <SmallDialog title="Demo dataset" onClose={onClose} onSave={() => onSave(draft)}>
      <Field
        label="Dataset version"
        value={draft.datasetVersion}
        onChange={(datasetVersion) => setDraft({ ...draft, datasetVersion })}
        hint="The seed every new demo tenant is cloned from."
      />
      <Field
        label="Wipe after idle"
        value={String(draft.idleMinutes)}
        onChange={(raw) => setDraft({
          ...draft, idleMinutes: Math.round(Number(raw.replace(/[^0-9]/g, '')) || 0),
        })}
        hint="Minutes. A demo tenant nobody has touched for this long is wiped."
      />
      <SwitchRow
        label="Wipe on sign-out"
        checked={draft.wipeOnSignOut}
        onChange={(wipeOnSignOut) => setDraft({ ...draft, wipeOnSignOut })}
      />
    </SmallDialog>
  )
}

function IdentityDialog({ identity, onClose, onSave }: {
  identity: Settings['identity']; onClose: () => void; onSave: (value: unknown) => void
}) {
  const [draft, setDraft] = useState(identity)
  return (
    <SmallDialog
      title="Product identity"
      note="Rowted’s own, not a client’s — this is what a client sees Rowted called."
      onClose={onClose}
      onSave={() => onSave(draft)}
      width={560}
    >
      <Field
        label="Product name"
        value={draft.productName}
        onChange={(productName) => setDraft({ ...draft, productName })}
      />
      <Field
        label="Support address"
        value={draft.supportAddress}
        onChange={(supportAddress) => setDraft({ ...draft, supportAddress })}
        type="email"
        hint="Reply-to on every email Rowted sends, and what the app offers as help."
      />
      <Field
        label="App URL"
        value={draft.appUrl}
        onChange={(appUrl) => setDraft({ ...draft, appUrl })}
        hint="Where a sign-in link lands. Must be https for webhooks to register."
      />
      <Field
        label="API URL"
        value={draft.apiUrl}
        onChange={(apiUrl) => setDraft({ ...draft, apiUrl })}
      />
      <Field
        label="Brand colour"
        value={draft.accentHex}
        onChange={(accentHex) => setDraft({ ...draft, accentHex })}
        hint="Six hex characters after a hash, like 2e7d4f."
      />
    </SmallDialog>
  )
}

/**
 * What somebody typed into the domains box, as a list.
 *
 * Commas **or** spaces, because a list typed by hand arrives with both, and a
 * box that accepts one separator and silently eats the other is the bug this
 * replaced. A leading `@` is dropped — the row outside prints `@rowted.com`,
 * so that is the form somebody copies back in — and the whole thing is
 * lower-cased and de-duplicated, because a domain is not case-sensitive and
 * `assertInvitableDomain` compares it folded.
 */
export function domainList(typed: string): string[] {
  return [...new Set(typed.split(/[\s,]+/)
    .map((one) => one.trim().toLowerCase().replace(/^@/, ''))
    .filter(Boolean))]
}

export function SystemDialog({ which, system, onClose, onSave }: {
  which: SystemEditor
  system: Settings['system']
  onClose: () => void
  onSave: (value: unknown) => void
}) {
  const [draft, setDraft] = useState(system)
  /*
   * The domains box holds **what was typed**, not what it parses to.
   *
   * It used to render from `draft.inviteDomains.join(', ')` on every
   * keystroke, and the parse that fed it dropped blanks: typing a comma made
   * `['rowted.com', '']`, the blank was filtered out, and the separator
   * vanished as it was pressed. So did a space. A field whose own separator
   * cannot be typed has room for exactly one value, which is why the box said
   * "comma separated" and could not hold two. `TrialsDialog`'s nudge-days box
   * is the same box and already does it this way.
   */
  const [typedDomains, setTypedDomains] = useState(system.inviteDomains.join(', '))

  const titles = {
    banner: 'Maintenance banner',
    ai: 'Rowted AI',
    signIn: 'Client sign-in policy',
    retention: 'Data retention',
    invites: 'Who can be invited',
    idle: 'Sign an admin out when idle',
  } as const

  return (
    <SmallDialog
      title={titles[which]}
      onClose={onClose}
      /* Parsed once, here, rather than on every keystroke — see above. */
      onSave={() => onSave(which === 'invites'
        ? { ...draft, inviteDomains: domainList(typedDomains) }
        : draft)}
    >
      {which === 'banner' && (
        <>
          <SwitchRow
            label="Show the banner"
            checked={draft.maintenanceBanner.on}
            onChange={(on) => setDraft({
              ...draft, maintenanceBanner: { ...draft.maintenanceBanner, on },
            })}
            hint="Every client sees it, at the top of every screen."
          />
          <Field
            label="What it says"
            value={draft.maintenanceBanner.text}
            onChange={(text) => setDraft({
              ...draft, maintenanceBanner: { ...draft.maintenanceBanner, text },
            })}
          />
          <SwitchRow
            label="Read-only mode"
            checked={draft.maintenanceBanner.readOnly}
            onChange={(readOnly) => setDraft({
              ...draft, maintenanceBanner: { ...draft.maintenanceBanner, readOnly },
            })}
            hint="Refuses every write across every client. Only for a migration."
          />
        </>
      )}

      {which === 'ai' && (
        <>
          <SwitchRow
            label="Rowted AI"
            checked={draft.aiEnabled}
            onChange={(aiEnabled) => setDraft({ ...draft, aiEnabled })}
            hint="Off hides the assistant and the smart-import suggestions from every client.
                  Column matching by name still works."
          />
          <p className="m-0 flex items-start gap-1.5 text-[11.5px] leading-[1.45] text-warn">
            <span className="mt-px shrink-0"><IconWarn size={13} /></span>
            <span>
              Turning it on does nothing until Azure OpenAI is connected under
              Integrations — the features stay hidden either way, which is the safe
              order.
            </span>
          </p>
        </>
      )}

      {which === 'signIn' && (
        <>
          <SelectField
            label="Two-step for office roles"
            value={draft.clientSignIn.officeMfa}
            onChange={(officeMfa) => setDraft({
              ...draft,
              clientSignIn: {
                ...draft.clientSignIn, officeMfa: officeMfa as 'required' | 'optional',
              },
            })}
            hint="What every client's office users must do. Crews sign in on a PIN either way."
          >
            <option value="required">Required</option>
            <option value="optional">Optional</option>
          </SelectField>
          <SwitchRow
            label="Crew PIN"
            checked={draft.clientSignIn.crewPin}
            onChange={(crewPin) => setDraft({
              ...draft, clientSignIn: { ...draft.clientSignIn, crewPin },
            })}
            hint="Off means a crew signs in with an address and a password on a shared iPad,
                  which is worse. Only turn it off if a client's policy demands it."
          />
        </>
      )}

      {which === 'invites' && (
        <Field
          label="Allowed domains"
          value={typedDomains}
          onChange={setTypedDomains}
          hint="Comma separated — rowted.com, koowali.com. An invite to any other address is
                refused. Leave it empty to allow any, which nothing recommends."
        />
      )}

      {which === 'idle' && (
        <Field
          label="Hours"
          value={String(draft.sessionIdleHours)}
          onChange={(raw) => setDraft({
            ...draft,
            sessionIdleHours: Math.max(1, Math.round(Number(raw.replace(/[^0-9]/g, '')) || 1)),
          })}
          hint="How long a Rowted Admin session survives without being used. Four is what
                §2.6 asks for; the absolute limit is separate and is not this."
        />
      )}

      {which === 'retention' && (
        <>
          <Field
            label="Audit log"
            value={String(draft.retention.auditYears)}
            onChange={(raw) => setDraft({
              ...draft,
              retention: {
                ...draft.retention,
                auditYears: Math.round(Number(raw.replace(/[^0-9]/g, '')) || 1),
              },
            })}
            hint="Years. Seven is what §D asks for, because financial records are kept
                  seven years."
          />
          <Field
            label="Files after a job closes"
            value={String(draft.retention.filesAfterCloseYears)}
            onChange={(raw) => setDraft({
              ...draft,
              retention: {
                ...draft.retention,
                filesAfterCloseYears: Math.round(Number(raw.replace(/[^0-9]/g, '')) || 0),
              },
            })}
            hint="Years. Photos are the evidence a job was done, so this is longer than it
                  feels like it needs to be."
          />
          <Field
            label="Demo tenants"
            value={String(draft.retention.demoDays)}
            onChange={(raw) => setDraft({
              ...draft,
              retention: {
                ...draft.retention,
                demoDays: Math.round(Number(raw.replace(/[^0-9]/g, '')) || 0),
              },
            })}
            hint="Days. Zero means a demo is wiped as soon as it goes idle."
          />
        </>
      )}
    </SmallDialog>
  )
}

/** The shape every one of the small ones takes, so they cannot drift. */
function SmallDialog({ title, note, width = 480, onClose, onSave, children }: {
  title: string
  note?: string
  width?: number
  onClose: () => void
  onSave: () => void
  children: ReactNode
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      width={width}
      footerNote={note ?? 'Saving asks for your sign-in code again.'}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={onSave}>Save</Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">{children}</div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Outbound email identity
// ---------------------------------------------------------------------------

/**
 * The Backups row — §5.4, addendum §I.5.
 *
 * "A backup nobody has restored is a hope, not a backup." What it reports is
 * the *restore*: a real dump, restored into a scratch database, its row
 * counts compared table for table against the source, and the integrity suite
 * run against the restored copy.
 *
 * **Four answers, not three.** "Nobody set this up" is grey and says which
 * variables are missing; "never run" and "longer ago than it should be" are
 * amber; a failure is red with the first reason on it. Painting an
 * unconfigured deployment red would teach everybody to ignore the colour,
 * which is the failure the DNS row above was fixed for.
 */
function Backups({ status, mayRun, running, onRun }: {
  status: BackupStatus | null
  mayRun: boolean
  running: boolean
  onRun: () => void
}) {
  const TONES: Record<BackupStatus['tone'], { label: string; tone: TagTone }> = {
    ok: { label: 'Restores', tone: 'ok' },
    warn: { label: 'Worth a look', tone: 'warn' },
    bad: { label: 'Needs attention', tone: 'bad' },
    off: { label: 'Not set up', tone: 'quiet' },
  }
  const word = status ? TONES[status.tone] : null

  return (
    <li className="border-b border-line-soft px-4 py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-cell font-medium text-text">Backups</span>
          <span className="block text-[11.5px] leading-[1.45] text-text-faint">
            {status?.note ?? 'nightly dump, restored and checked'}
          </span>
        </span>
        <span className="shrink-0 text-cell text-text-body">
          {word
            ? <Tag tone={word.tone}>{word.label}</Tag>
            : <span className="text-text-faint">loading</span>}
        </span>
        {mayRun && (
          <Button onClick={onRun} disabled={running || !status?.configured}>
            {running ? <IconSpinner size={15} /> : <IconCheck size={15} />}
            Restore drill
          </Button>
        )}
      </div>

      {/*
        * Every reason, not the first one. The row's sentence carries one
        * because a row is one line; somebody who has opened the page to find
        * out why needs the list.
        */}
      {status?.lastRun && status.lastRun.failures.length > 0 && (
        <ul className="m-0 mt-2 list-none rounded-field bg-surface-alt p-0">
          {status.lastRun.failures.map((failure) => (
            <li key={failure}
              className="border-b border-line-soft px-3 py-2 text-[11.5px] leading-[1.45]
                         text-danger last:border-b-0">
              {failure}
            </li>
          ))}
        </ul>
      )}

      {status?.lastRun?.ok && (
        <p className="m-0 mt-1.5 text-[11.5px] text-text-faint">
          Took {status.lastRun.seconds}s
          {status.lastRun.byName ? ` · started by ${status.lastRun.byName}` : ' · nightly'}
        </p>
      )}
    </li>
  )
}

/**
 * SPF, DKIM and DMARC on the sending domain — §5.4.
 *
 * Not checked until somebody presses **Re-check**, because three DNS lookups
 * would make the page take seconds to open, and this is a thing you ask about
 * rather than a thing you watch.
 *
 * It is **advice, not a gate**. Nothing here stops a message sending; what it
 * prevents is invoices quietly landing in junk folders and being reported
 * weeks later as "we never got it".
 */
function MailIdentity({ identity, checking, onCheck }: {
  identity: EmailIdentity | null
  checking: boolean
  onCheck: () => void
}) {
  /*
   * `unknown` counts against passing, not for it.
   *
   * A lookup that could not run — no mailbox connected, a resolver that
   * refused — is not a domain that is fine. The first version of this looked
   * only for bad and warn, so a check that never ran read as **All passing**,
   * which is the one answer this row must never give wrongly.
   */
  const worst = (['bad', 'warn', 'unknown'] as const)
    .map((status) => identity?.checks.find((one) => one.status === status))
    .find(Boolean)

  const word: { label: string; tone: TagTone } | null = !identity
    ? null
    : !worst ? { label: 'All passing', tone: 'ok' }
      : worst.status === 'bad' ? { label: 'Needs attention', tone: 'bad' }
        : worst.status === 'warn' ? { label: 'Could be stronger', tone: 'warn' }
          : { label: 'Could not check', tone: 'quiet' }

  return (
    <li className="border-b border-line-soft px-4 py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-cell font-medium text-text">Outbound email identity</span>
          <span className="block text-[11.5px] leading-[1.45] text-text-faint">
            SPF · DKIM · DMARC{identity?.domain ? ` on ${identity.domain}` : ''}
          </span>
        </span>
        <span className="shrink-0 text-cell text-text-body">
          {word
            ? <Tag tone={word.tone}>{word.label}</Tag>
            : <span className="text-text-faint">not checked</span>}
        </span>
        <Button onClick={onCheck} disabled={checking}>
          {checking ? <IconSpinner size={15} /> : <IconCheck size={15} />}
          Re-check
        </Button>
      </div>

      {identity && (
        <ul className="m-0 mt-2 list-none rounded-field bg-surface-alt p-0">
          {identity.checks.map((check) => (
            <li key={check.name}
              className="flex items-start gap-2 border-b border-line-soft px-3 py-2
                         last:border-b-0">
              <span className="w-[54px] shrink-0 font-mono text-[11.5px] text-text">
                {check.name}
              </span>
              <span className={cn('min-w-0 flex-1 text-[11.5px] leading-[1.45]',
                check.status === 'bad' ? 'text-danger'
                  : check.status === 'warn' ? 'text-warn'
                    : check.status === 'unknown' ? 'text-text-faint' : 'text-text-muted')}>
                {check.detail}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * A switch with a sentence under it.
 *
 * `Switch` takes a label and nothing else, which is right for a row in a form
 * where the label *is* the whole question. Every switch on this page decides
 * something for every client of the product, and each one needs a clause
 * saying what that means — so the pairing lives here rather than growing
 * `Switch` a `hint` that most of its callers would never pass.
 */
function SwitchRow({ label, checked, onChange, hint }: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  hint?: string
}) {
  return (
    <span className="flex flex-col gap-1">
      <Switch label={label} checked={checked} onChange={onChange} />
      {hint && (
        <span className="text-[11.5px] leading-[1.45] text-text-muted">{hint}</span>
      )}
    </span>
  )
}
